import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import { enqueueSnackbar } from "notistack";
import { apiUrl } from "./LoginSignup";
import { downloadInvoiceReportExcel } from "../utils/invoiceReportExport";
import { formatGstNo } from "../utils/gstFormat";
import { getCurrentMonthDateRange } from "../utils/dateFormat";
import "./AllInvoices.css";

const INVOICE_ACCESS_ROLES = ["dev", "srdev"];

const getDefaultDateRange = () => getCurrentMonthDateRange();

const parseAmount = (value) =>
  Number(String(value || "").replace(/[₹,\s]/g, "")) || 0;

const InvoiceMobileCard = ({ row }) => (
  <Paper className="invoice-mobile-card" elevation={0}>
    <Box className="invoice-mobile-card-header">
      <Typography className="invoice-mobile-card-title">
        {row.clientName}
      </Typography>
      <span className="invoice-mobile-card-badge">{row.invoiceNo}</span>
    </Box>
    <Box className="invoice-mobile-card-grid">
      <Box className="invoice-mobile-field">
        <span className="invoice-mobile-label">Date</span>
        <span className="invoice-mobile-value">{row.date}</span>
      </Box>
      <Box className="invoice-mobile-field">
        <span className="invoice-mobile-label">Sr. No.</span>
        <span className="invoice-mobile-value">{row.serialNo}</span>
      </Box>
      <Box className="invoice-mobile-field full-width">
        <span className="invoice-mobile-label">Company</span>
        <span className="invoice-mobile-value">{row.companyName}</span>
      </Box>
      <Box className="invoice-mobile-field">
        <span className="invoice-mobile-label">GST No.</span>
        <span className="invoice-mobile-value">{row.gstNo}</span>
      </Box>
      <Box className="invoice-mobile-field">
        <span className="invoice-mobile-label">SAC Code</span>
        <span className="invoice-mobile-value">{row.sacCode}</span>
      </Box>
      <Box className="invoice-mobile-field">
        <span className="invoice-mobile-label">Amount</span>
        <span className="invoice-mobile-value">{row.amount}</span>
      </Box>
      <Box className="invoice-mobile-field">
        <span className="invoice-mobile-label">Without GST</span>
        <span className="invoice-mobile-value">{row.withoutGst}</span>
      </Box>
      <Box className="invoice-mobile-field">
        <span className="invoice-mobile-label">GST %</span>
        <span className="invoice-mobile-value">{row.gst}</span>
      </Box>
    </Box>
  </Paper>
);

const AllInvoices = () => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const userSession = JSON.parse(localStorage.getItem("userSession") || "{}");
  const hasAccess = INVOICE_ACCESS_ROLES.includes(userSession.user_role);

  const defaults = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInvoices = useCallback(async (options = {}) => {
    const { silent = false } = options;

    if (!startDate || !endDate) {
      if (!silent) {
        enqueueSnackbar("Please select both start and end dates.", {
          variant: "warning",
        });
      }
      return null;
    }

    if (new Date(startDate) > new Date(endDate)) {
      if (!silent) {
        enqueueSnackbar("Start date cannot be after end date.", {
          variant: "warning",
        });
      }
      return null;
    }

    const userSession = JSON.parse(localStorage.getItem("userSession") || "{}");
    if (!userSession?.token) {
      if (!silent) {
        enqueueSnackbar("Please log in again.", { variant: "error" });
      }
      return null;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        userId: userSession.user_id || "",
        userRole: userSession.user_role || "",
      });
      const response = await fetch(
        `${apiUrl}/booking/invoices/report?${params.toString()}`,
        {
          headers: {
            authorization: userSession.token,
          },
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(
            "Invoice report API is not available. Restart or redeploy the backend server."
          );
        }
        throw new Error(data.message || "Failed to load invoices");
      }

      const rows = (data.invoices || []).map((row) => ({
        ...row,
        gstNo: formatGstNo(row.gstNo),
      }));
      setInvoices(rows);
      return rows;
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to load invoices", {
        variant: "error",
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchInvoices({ silent: true });
  }, [fetchInvoices, hasAccess]);

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSearch = async () => {
    const rows = await fetchInvoices();
    if (rows) {
      enqueueSnackbar(`Loaded ${rows.length} invoice(s).`, { variant: "success" });
    }
  };

  const handleDownload = async () => {
    let rows = invoices;

    if (!rows.length) {
      rows = await fetchInvoices();
    }

    if (!rows?.length) {
      enqueueSnackbar("No invoices found for the selected date range.", {
        variant: "info",
      });
      return;
    }

    const fileName = `invoices_${startDate}_to_${endDate}.xlsx`;
    downloadInvoiceReportExcel(rows, fileName);
    enqueueSnackbar("Invoice Excel downloaded successfully.", {
      variant: "success",
    });
  };

  const totalAmount = invoices.reduce(
    (sum, row) => sum + parseAmount(row.amount),
    0
  );

  return (
    <Box className="all-invoices-page">
      <Typography variant="h4" className="all-invoices-title">
        All Invoices
      </Typography>

      <Paper className="all-invoices-summary" elevation={0}>
        <Box className="summary-card">
          <Typography variant="body2" color="text.secondary">
            Total Records
          </Typography>
          <Typography variant="h5">{invoices.length}</Typography>
        </Box>
        <Box className="summary-card">
          <Typography variant="body2" color="text.secondary">
            Total Amount (with GST)
          </Typography>
          <Typography variant="h5">
            ₹{totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Box className="summary-card">
          <Typography variant="body2" color="text.secondary">
            Filtered By
          </Typography>
          <Typography variant="h6">
            Booking date: {startDate} to {endDate}
          </Typography>
        </Box>
      </Paper>

      <Paper className="all-invoices-filters" elevation={0}>
        <TextField
          label="From Date (Booking Date)"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="To Date (Booking Date)"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={loading}
        >
          Download Excel
        </Button>
      </Paper>

      <TableContainer component={Paper} className="all-invoices-table-wrap">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>SR. NO.</TableCell>
              <TableCell>DATE</TableCell>
              <TableCell>SAC CODE</TableCell>
              <TableCell>CLIENT NAME</TableCell>
              <TableCell>GST NO.</TableCell>
              <TableCell align="right">AMOUNT</TableCell>
              <TableCell align="right">GST</TableCell>
              <TableCell align="right">WITHOUT GST</TableCell>
              <TableCell>INVOICE NO.</TableCell>
              <TableCell>COMPANY NAME</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Loading invoices...
                </TableCell>
              </TableRow>
            ) : invoices.length > 0 ? (
              invoices.map((row) => (
                <TableRow
                  key={row.bookingId || `${row.invoiceNo}-${row.serialNo}`}
                  hover
                >
                  <TableCell>{row.serialNo}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.sacCode}</TableCell>
                  <TableCell>{row.clientName}</TableCell>
                  <TableCell>{row.gstNo}</TableCell>
                  <TableCell align="right">{row.amount}</TableCell>
                  <TableCell align="right">{row.gst}</TableCell>
                  <TableCell align="right">{row.withoutGst}</TableCell>
                  <TableCell>{row.invoiceNo}</TableCell>
                  <TableCell>{row.companyName}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No invoices found for booking dates in {startDate} to {endDate}.
                  Change the date range to view older or all invoices.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="all-invoices-mobile-list" aria-hidden={!isMobileView}>
        {loading ? (
          <Typography className="invoice-mobile-empty">Loading invoices...</Typography>
        ) : invoices.length > 0 ? (
          invoices.map((row) => (
            <InvoiceMobileCard
              key={row.bookingId || `${row.invoiceNo}-${row.serialNo}`}
              row={row}
            />
          ))
        ) : (
          <Typography className="invoice-mobile-empty">
            No invoices found for booking dates in {startDate} to {endDate}.
            Change the date range to view older or all invoices.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AllInvoices;
